import {
    applyAsRepairAutoValuesToForm,
    computeAsRepairAutoValue,
    formatAsRepairAutoDisplay,
    normalizeAsRepairToken,
} from './asRepairCalculations';
import { toHtmlDateValue } from './specDateValue';

export const computeAsRepairAutoValueFromConfig = (config, field, values) => {
    try {
        return computeAsRepairAutoValue(
            field,
            values,
            normalizeAsRepairFormConfig(config).fields,
        );
    } catch (error) {
        return '';
    }
};

export { applyAsRepairAutoValuesToForm, formatAsRepairAutoDisplay };

export const AS_REPAIR_BUILTIN_KEYS = new Set([
    'siteName',
    'productName',
    'receivedDate',
    'status',
    'symptom',
    'assignee',
    'memo',
]);

export const AS_REPAIR_STATUS_OPTIONS = [
    { value: 'received', label: '접수' },
    { value: 'in_progress', label: '진행중' },
    { value: 'done', label: '완료' },
];

export const buildDefaultAsRepairFormFields = () => [
    { key: 'siteName', label: '현장명', inputType: 'text', required: true },
    { key: 'productName', label: '제품명(모델명)', inputType: 'text', required: true },
    { key: 'receivedDate', label: '접수일', inputType: 'date', required: false },
    { key: 'status', label: '상태', inputType: 'status', required: false },
    { key: 'symptom', label: '증상 / 요청내용', inputType: 'textarea', required: false },
    { key: 'assignee', label: '담당자', inputType: 'text', required: false },
    { key: 'memo', label: '비고', inputType: 'text', required: false },
];

export const buildDefaultAsRepairFormConfig = () => ({
    fields: buildDefaultAsRepairFormFields(),
});

export const normalizeAsRepairInputType = (raw) => {
    const t = String(raw || 'text').trim().toLowerCase();
    if (t === 'textarea' || t === '장문' || t === 'multiline') {
        return 'textarea';
    }
    if (t === 'date' || t === '날짜' || t === '날짜입력') {
        return 'date';
    }
    if (t === 'status' || t === '상태') {
        return 'status';
    }
    if (t === 'dropdown' || t === '드롭다운') {
        return 'dropdown';
    }
    if (t === 'quantity' || t === '수량' || t === '수량입력') {
        return 'quantity';
    }
    if (t === 'auto' || t === '자동' || t === '자동입력' || t === 'auto_calc' || t === 'calculated') {
        return 'auto';
    }
    return 'text';
};

export const parseQuantityValue = (raw, { min = 0, max = 999999 } = {}) => {
    if (raw === '' || raw === null || raw === undefined) {
        return min;
    }
    const n = Number.parseInt(String(raw).replace(/[^\d-]/g, ''), 10);
    if (Number.isNaN(n)) {
        return min;
    }
    return Math.min(max, Math.max(min, n));
};

export const formatQuantityDisplay = (raw) => String(parseQuantityValue(raw));

/** 수량 0·빈 값은 화면에 빈칸, 1 이상만 숫자 표시 */
export const formatAsRepairQuantityDisplay = (raw) => {
    if (raw === '' || raw === null || raw === undefined) {
        return '';
    }
    const n = parseQuantityValue(raw);
    return n === 0 ? '' : String(n);
};

/** 목록 요약에도 세부사항에 항상 표시할 수량 항목 */
export const shouldAlwaysShowAsRepairDetailField = (field) => {
    const label = String(field?.label || '').replace(/\s+/g, '');
    return label.includes('실입고수량')
        || label.includes('진행중수량')
        || label.includes('미완료수량')
        || label.includes('미완료')
        || label.includes('수리완료수량')
        || label.includes('수리완료')
        || label.includes('완료수량');
};

const slugKey = (label, index) => {
    const base = String(label || '')
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_가-힣]/g, '')
        .slice(0, 40);
    return base ? `custom_${base}` : `custom_${index + 1}`;
};

export const normalizeAsRepairFormField = (raw, index = 0) => {
    let key = String(raw?.key || '').trim();
    const label = String(raw?.label || raw?.title || '').trim();
    const inputType = normalizeAsRepairInputType(raw?.inputType);
    const required = !!raw?.required;

    if (!label) {
        return null;
    }

    if (!key) {
        key = slugKey(label, index);
    }

    const base = { key, label, inputType, required };
    if (inputType === 'auto') {
        return {
            ...base,
            formula: String(raw?.formula || '').trim(),
        };
    }
    return base;
};

export const normalizeAsRepairFormConfig = (raw) => {
    const fields = Array.isArray(raw?.fields)
        ? raw.fields.map((f, i) => normalizeAsRepairFormField(f, i)).filter(Boolean)
        : [];
    if (fields.length === 0) {
        return buildDefaultAsRepairFormConfig();
    }
    return { fields };
};

export const buildEmptyAsRepairFormValues = (config) => {
    const values = {};
    normalizeAsRepairFormConfig(config).fields.forEach((field) => {
        if (field.inputType === 'status') {
            values[field.key] = 'received';
        } else if (field.inputType === 'quantity') {
            values[field.key] = 0;
        } else if (field.inputType === 'auto') {
            values[field.key] = '';
        } else {
            values[field.key] = '';
        }
    });
    return values;
};

export const validateAsRepairFormValues = (config, values) => {
    const normalized = normalizeAsRepairFormConfig(config);
    for (const field of normalized.fields) {
        if (!field.required || field.inputType === 'auto') {
            continue;
        }
        const v = values[field.key];
        if (field.inputType === 'status') {
            if (!String(v || '').trim()) {
                return `${field.label}은(는) 필수입니다.`;
            }
            continue;
        }
        if (field.inputType === 'quantity') {
            if (v === '' || v === null || v === undefined) {
                return `${field.label}은(는) 필수입니다.`;
            }
            continue;
        }
        if (v !== 0 && !String(v ?? '').trim()) {
            return `${field.label}은(는) 필수입니다.`;
        }
    }
    return null;
};

const parseDateOnly = (raw) => {
    if (raw === '' || raw == null) {
        return null;
    }
    const s = String(raw).trim().slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};

export const splitAsRepairPayload = (config, values) => {
    const normalized = normalizeAsRepairFormConfig(config);
    const known = {
        siteName: '',
        productName: '',
        receivedDate: null,
        status: 'received',
        symptom: '',
        assignee: '',
        memo: '',
    };
    const extra = {};

    normalized.fields.forEach((field) => {
        const raw = values[field.key];
        const { key, inputType } = field;
        if (key === 'receivedDate') {
            if (inputType === 'date') {
                known.receivedDate = parseDateOnly(raw);
            } else {
                extra[key] = raw ?? '';
            }
            return;
        }
        if (key === 'status') {
            if (inputType === 'status') {
                known.status = raw || 'received';
            } else if (inputType === 'quantity') {
                extra[key] = parseQuantityValue(raw);
            } else {
                extra[key] = raw ?? '';
            }
            return;
        }
        if (AS_REPAIR_BUILTIN_KEYS.has(key)) {
            known[key] = String(raw ?? '').trim();
            return;
        }
        if (inputType === 'quantity') {
            extra[key] = parseQuantityValue(raw);
        } else if (inputType === 'auto') {
            extra[key] = computeAsRepairAutoValueFromConfig(config, field, values);
        } else {
            extra[key] = raw ?? '';
        }
    });

    return { known, extra };
};

const normalizeLabelKey = (label) => String(label || '').replace(/\s+/g, '').toLowerCase();

const pickAsRepairField = (fields, { labelIncludes = [], keys = [], inputType = null, excludeKeys = new Set() }) => {
    const pool = fields.filter((f) => !excludeKeys.has(f.key));
    for (const hint of labelIncludes) {
        const h = normalizeLabelKey(hint);
        const found = pool.find((f) => normalizeLabelKey(f.label).includes(h));
        if (found) {
            return found;
        }
    }
    for (const key of keys) {
        const found = pool.find((f) => f.key === key);
        if (found) {
            return found;
        }
    }
    if (inputType) {
        const found = pool.find((f) => f.inputType === inputType);
        if (found) {
            return found;
        }
    }
    return null;
};

/** 목록 접힘 상태 요약 열 정의 (A/S 리스트 설정 라벨·키에 맞춰 자동 매칭) */
export const AS_REPAIR_LIST_SUMMARY_SPECS = [
    { id: 'boxNumber', title: '박스번호', labelIncludes: ['박스번호', '박스'], keys: [] },
    { id: 'modelName', title: '모델명', labelIncludes: ['품목코드', '모델명', '모델'], keys: ['productName'] },
    { id: 'siteName', title: '현장명', labelIncludes: ['현장명', '현장'], keys: ['siteName'] },
    { id: 'totalQty', title: '실입고 수량', labelIncludes: ['실입고수량', '실입고'], inputType: 'quantity' },
    { id: 'inProgressQty', title: '진행중 수량', labelIncludes: ['진행중수량', '진행중'], inputType: 'quantity' },
    { id: 'doneQty', title: '수리완료 수량', labelIncludes: ['수리완료수량', '수리완료', '완료수량'], inputType: 'quantity' },
];

export const buildAsRepairListSummary = (config) => {
    const fields = normalizeAsRepairFormConfig(config).fields;
    const usedKeys = new Set();
    const columns = AS_REPAIR_LIST_SUMMARY_SPECS.map((spec) => {
        const field = pickAsRepairField(fields, {
            labelIncludes: spec.labelIncludes,
            keys: spec.keys,
            inputType: spec.inputType,
            excludeKeys: usedKeys,
        });
        if (field) {
            usedKeys.add(field.key);
        }
        return {
            ...spec,
            field,
            displayLabel: field?.label || spec.title,
        };
    });

    const remainingQty = fields.filter((f) => f.inputType === 'quantity' && !usedKeys.has(f.key));
    const totalCol = columns.find((c) => c.id === 'totalQty');
    const doneCol = columns.find((c) => c.id === 'doneQty');
    if (!totalCol.field && remainingQty[0]) {
        totalCol.field = remainingQty[0];
        totalCol.displayLabel = remainingQty[0].label || totalCol.title;
        usedKeys.add(remainingQty[0].key);
    }
    if (!doneCol.field && remainingQty[1]) {
        doneCol.field = remainingQty[1];
        doneCol.displayLabel = remainingQty[1].label || doneCol.title;
        usedKeys.add(remainingQty[1].key);
    }

    return { columns, summaryKeys: usedKeys };
};

export const formatAsRepairFieldDisplayText = (field, row, config = null) => {
    if (!field) {
        return '-';
    }
    if (field.inputType === 'auto' && config) {
        const values = asRepairRowToFormValues(row, config);
        return formatAsRepairAutoDisplay(computeAsRepairAutoValueFromConfig(config, field, values));
    }
    const val = getAsRepairFieldDisplayValue(field, row, config);
    if (field.inputType === 'quantity') {
        return formatAsRepairQuantityDisplay(val);
    }
    if (val == null) {
        return '-';
    }
    if (field.key === 'receivedDate' && field.inputType === 'date') {
        return String(val).slice(0, 10) || '-';
    }
    if (field.inputType === 'status') {
        const opt = AS_REPAIR_STATUS_OPTIONS.find((o) => o.value === val);
        return opt?.label || String(val);
    }
    return String(val);
};

/** DB·extra에 저장된 원시 값 (자동 입력 제외) */
const hasNonEmptyAsRepairValue = (value) => (
    value === 0 || (value != null && String(value).trim() !== '')
);

const findFallbackExtraValueByFieldLabel = (field, row) => {
    const extra = row?.extra && typeof row.extra === 'object' ? row.extra : null;
    if (!extra) {
        return null;
    }
    const labelToken = normalizeAsRepairToken(field?.label);
    if (!labelToken) {
        return null;
    }
    const candidates = Object.entries(extra).filter(([k, v]) => {
        if (!hasNonEmptyAsRepairValue(v)) {
            return false;
        }
        const keyToken = normalizeAsRepairToken(k).replace(/^custom_/, '');
        return keyToken.includes(labelToken) || labelToken.includes(keyToken);
    });
    if (candidates.length === 0) {
        return null;
    }
    // 키명이 라벨과 가장 가까운 항목을 우선 선택
    candidates.sort((a, b) => {
        const aToken = normalizeAsRepairToken(a[0]).replace(/^custom_/, '');
        const bToken = normalizeAsRepairToken(b[0]).replace(/^custom_/, '');
        return Math.abs(aToken.length - labelToken.length) - Math.abs(bToken.length - labelToken.length);
    });
    return candidates[0][1];
};

export const getRawAsRepairFieldValue = (field, row) => {
    const { key, inputType } = field;
    if (key === 'receivedDate' && inputType !== 'date') {
        const v = row.extra?.[key];
        return hasNonEmptyAsRepairValue(v) ? v : null;
    }
    if (key === 'status' && inputType !== 'status') {
        const v = row.extra?.[key];
        return hasNonEmptyAsRepairValue(v) ? v : null;
    }
    if (AS_REPAIR_BUILTIN_KEYS.has(key)) {
        const v = row[key];
        return hasNonEmptyAsRepairValue(v) ? v : null;
    }
    const v = row.extra?.[key];
    if (hasNonEmptyAsRepairValue(v)) {
        return v;
    }
    return findFallbackExtraValueByFieldLabel(field, row);
};

/** 목록/상세 표시용 — inputType에 따라 DB 컬럼 또는 extra 값 */
export const getAsRepairFieldDisplayValue = (field, row, config = null) => {
    const { key, inputType } = field;
    if (inputType === 'auto') {
        if (config) {
            const values = asRepairRowToFormValues(row, config);
            const computed = computeAsRepairAutoValueFromConfig(config, field, values);
            if (computed !== '' && computed !== null && computed !== undefined) {
                return computed;
            }
        }
        const stored = row.extra?.[key];
        return stored === 0 || (stored != null && String(stored).trim() !== '') ? stored : null;
    }
    return getRawAsRepairFieldValue(field, row);
};

export const createInitialAsRepairFilters = () => ([
    { fieldKey: '', queries: [''] },
    { fieldKey: '', queries: [''] },
    { fieldKey: '', queries: [''] },
]);

/** A/S 리스트 설정 항목 — 필터 드롭다운 옵션 */
export const getAsRepairFilterFieldOptions = (config) => (
    normalizeAsRepairFormConfig(config).fields.map((field) => ({
        key: field.key,
        label: field.label,
    }))
);

const asRepairRowSearchBlob = (row, config) => {
    const fields = normalizeAsRepairFormConfig(config).fields;
    return fields
        .map((field) => formatAsRepairFieldDisplayText(field, row, config))
        .filter((text) => text && text !== '-')
        .join(' ')
        .toLowerCase();
};

const getAsRepairFilterQueries = (filter) => {
    if (Array.isArray(filter?.queries)) {
        return filter.queries.map((q) => String(q ?? '').trim().toLowerCase()).filter(Boolean);
    }
    const legacy = String(filter?.query || '').trim().toLowerCase();
    return legacy ? [legacy] : [];
};

export const rowMatchesAsRepairFilters = (row, config, filters, fieldByKeyOverride = null) => {
    const fields = fieldByKeyOverride
        ? null
        : normalizeAsRepairFormConfig(config).fields;
    const fieldByKey = fieldByKeyOverride || Object.fromEntries(fields.map((f) => [f.key, f]));

    return (filters || []).every((filter) => {
        const fieldKey = String(filter?.fieldKey || '').trim();
        const queries = getAsRepairFilterQueries(filter);
        if (!fieldKey || queries.length === 0) {
            return true;
        }
        const field = fieldByKey[fieldKey];
        if (!field) {
            return true;
        }
        // 검색은 raw/stored 값 우선 (auto formula 전체 재계산 회피)
        let text = '';
        if (field.inputType === 'auto') {
            const stored = row?.extra?.[field.key];
            if (stored === 0 || (stored != null && String(stored).trim() !== '')) {
                text = String(stored).toLowerCase();
            } else {
                text = formatAsRepairFieldDisplayText(field, row, config).toLowerCase();
            }
        } else {
            const raw = getRawAsRepairFieldValue(field, row);
            if (raw === 0 || (raw != null && String(raw).trim() !== '')) {
                text = String(raw).toLowerCase();
            }
        }
        // 같은 항목의 여러 검색어는 OR (하나라도 일치하면 포함)
        return queries.some((query) => text.includes(query));
    });
};

export const filterAsRepairRows = (rows, config, filters) => {
    const list = Array.isArray(rows) ? rows : [];
    if (list.length === 0) {
        return list;
    }
    const hasActive = (filters || []).some((filter) => {
        const fieldKey = String(filter?.fieldKey || '').trim();
        return fieldKey && getAsRepairFilterQueries(filter).length > 0;
    });
    if (!hasActive) {
        return list;
    }
    const fields = normalizeAsRepairFormConfig(config).fields;
    const fieldByKey = Object.fromEntries(fields.map((f) => [f.key, f]));
    return list.filter((row) => rowMatchesAsRepairFilters(row, config, filters, fieldByKey));
};

/** A/S 설정에서 출고일자(출고일) 필드 찾기 */
export const getAsRepairShipDateField = (config) => {
    const fields = normalizeAsRepairFormConfig(config).fields;
    return fields.find((field) => normalizeAsRepairToken(field?.label).includes('출고')) || null;
};

export const getAsRepairRowShipDateComparable = (row, shipDateField) => {
    if (!shipDateField || !row) {
        return null;
    }
    const raw = getRawAsRepairFieldValue(shipDateField, row);
    if (raw == null) {
        return null;
    }
    const normalized = toHtmlDateValue(raw);
    return normalized || null;
};

/** 출고일자 기간 필터 — from/to 중 하나라도 있으면 적용 (양쪽 모두 포함, 순서 뒤바뀌면 자동 보정) */
export const filterAsRepairRowsByShipDateRange = (rows, config, fromDate, toDate) => {
    const list = Array.isArray(rows) ? rows : [];
    let rangeFrom = toHtmlDateValue(fromDate);
    let rangeTo = toHtmlDateValue(toDate);
    if (!rangeFrom && !rangeTo) {
        return list;
    }
    if (rangeFrom && rangeTo && rangeFrom > rangeTo) {
        [rangeFrom, rangeTo] = [rangeTo, rangeFrom];
    }
    const shipDateField = getAsRepairShipDateField(config);
    if (!shipDateField) {
        return list;
    }
    return list.filter((row) => {
        const shipDate = getAsRepairRowShipDateComparable(row, shipDateField);
        if (!shipDate) {
            return false;
        }
        if (rangeFrom && shipDate < rangeFrom) {
            return false;
        }
        if (rangeTo && shipDate > rangeTo) {
            return false;
        }
        return true;
    });
};

/** 전체·진행중·수리완료 수량 필드 (목록 요약 설정 기준) */
export const getAsRepairProgressQtyFields = (listSummary) => {
    const totalCol = listSummary?.columns?.find((c) => c.id === 'totalQty');
    const doneCol = listSummary?.columns?.find((c) => c.id === 'doneQty');
    const inProgressCol = listSummary?.columns?.find((c) => c.id === 'inProgressQty');
    return {
        totalField: totalCol?.field || null,
        doneField: doneCol?.field || null,
        inProgressField: inProgressCol?.field || null,
    };
};

const AS_REPAIR_QTY_SHARE_COLORS = {
    totalQty: '#3b82f6',
    shortage: '#f97316',
    inProgressQty: '#f59e0b',
    doneQty: '#22c55e',
};

const findAsRepairQtyFieldByHints = (fields, labelIncludes, excludeKeys = new Set()) => {
    const skipDetailLike = (field) => {
        const token = normalizeLabelKey(field?.label);
        const keyToken = normalizeLabelKey(field?.key).replace(/^custom_/, '');
        // 하위 세부 항목은 상위(유상/무상) 집계 필드로 잡히지 않게 제외
        if (token.includes('micom') || keyToken.includes('micom')
            || token.includes('마이컴') || keyToken.includes('마이컴')) {
            return true;
        }
        return false;
    };
    const qty = pickAsRepairField(fields, {
        labelIncludes,
        inputType: 'quantity',
        excludeKeys,
    });
    if (qty && !skipDetailLike(qty)) {
        return qty;
    }
    const auto = pickAsRepairField(fields, {
        labelIncludes,
        inputType: 'auto',
        excludeKeys,
    });
    if (auto && !skipDetailLike(auto)) {
        return auto;
    }
    // pick이 세부 항목을 집었으면 힌트를 더 구체적으로 재시도
    const strictHints = labelIncludes.filter((hint) => (
        normalizeLabelKey(hint).includes('수량') || normalizeLabelKey(hint).length >= 4
    ));
    if (strictHints.length > 0) {
        const strictQty = pickAsRepairField(fields, {
            labelIncludes: strictHints,
            inputType: 'quantity',
            excludeKeys: new Set([
                ...excludeKeys,
                ...fields.filter(skipDetailLike).map((f) => f.key),
            ]),
        });
        if (strictQty) {
            return strictQty;
        }
    }
    return null;
};

/** 실입고 수량 필드 (목록 요약·설정 라벨 기준) */
export const findAsRepairReceivedQtyField = (config, listSummary) => {
    const fields = normalizeAsRepairFormConfig(config).fields;
    const fromLabel = pickAsRepairField(fields, {
        labelIncludes: ['실입고수량', '실입고'],
    });
    if (fromLabel) {
        return fromLabel;
    }
    const { totalField } = getAsRepairProgressQtyFields(listSummary);
    if (totalField) {
        const nl = normalizeLabelKey(totalField.label);
        if (nl.includes('실입고') || (nl.includes('입고') && !nl.includes('전체'))) {
            return totalField;
        }
    }
    const fromIncoming = pickAsRepairField(fields, {
        labelIncludes: ['입고수량', '입고'],
    });
    if (fromIncoming) {
        return fromIncoming;
    }
    return totalField || null;
};

/** 실입고 대비 수량부족·반품·유상·무상 수량 필드 (A/S 리스트 설정 라벨 기준) */
export const getAsRepairInboundBreakdownFields = (config, listSummary) => {
    const fields = normalizeAsRepairFormConfig(config).fields;
    const usedKeys = new Set();
    const { doneField } = getAsRepairProgressQtyFields(listSummary);
    const receivedField = findAsRepairReceivedQtyField(config, listSummary);
    if (receivedField?.key) {
        usedKeys.add(receivedField.key);
    }

    const shortageField = findAsRepairQtyFieldByHints(
        fields,
        ['수량부족', '부족수량', '부족'],
        usedKeys,
    );
    if (shortageField?.key) {
        usedKeys.add(shortageField.key);
    }

    const returnField = findAsRepairQtyFieldByHints(
        fields,
        ['반품수량', '반품'],
        usedKeys,
    );
    if (returnField?.key) {
        usedKeys.add(returnField.key);
    }

    const paidField = findAsRepairQtyFieldByHints(
        fields,
        ['유상수리수량', '유상수리', '유상'],
        usedKeys,
    );
    if (paidField?.key) {
        usedKeys.add(paidField.key);
    }

    const freeField = findAsRepairQtyFieldByHints(
        fields,
        ['무상수리수량', '무상수리', '무상'],
        usedKeys,
    );

    return {
        receivedField,
        doneField,
        shortageField,
        returnField,
        paidField,
        freeField,
    };
};

const sumAsRepairQtyAcrossRows = (rows, field, config) => {
    if (!field) {
        return 0;
    }
    const list = Array.isArray(rows) ? rows : [];
    return list.reduce(
        (sum, row) => sum + parseQuantityValue(
            getAsRepairFieldDisplayValue(field, row, config) ?? 0,
        ),
        0,
    );
};

const AS_REPAIR_INBOUND_DETAIL_COLORS = {
    shortage: '#f97316',
    return: '#ef4444',
    paid: '#8b5cf6',
    free: '#14b8a6',
};

/** 검색 결과 — 실입고 수량 대비 수량부족·반품·유상·무상 수리 비중 */
export const aggregateAsRepairInboundBreakdown = (rows, listSummary, config, options = {}) => {
    const { managementScope = AS_REPAIR_MANAGEMENT_SCOPE.REPAIR } = options;
    const isPersonalScope = managementScope === AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL;
    const {
        receivedField,
        doneField,
        shortageField,
        returnField,
        paidField,
        freeField,
    } = getAsRepairInboundBreakdownFields(
        config,
        listSummary,
    );
    const receivedTotal = sumAsRepairQtyAcrossRows(rows, receivedField, config);
    const defs = isPersonalScope
        ? [
            { id: 'shortage', title: '수량부족', field: shortageField, color: AS_REPAIR_INBOUND_DETAIL_COLORS.shortage },
            { id: 'return', title: '반품수량', field: returnField, color: AS_REPAIR_INBOUND_DETAIL_COLORS.return },
            { id: 'paid', title: '유상수리수량', field: paidField, color: AS_REPAIR_INBOUND_DETAIL_COLORS.paid },
            { id: 'free', title: '무상수리수량', field: freeField, color: AS_REPAIR_INBOUND_DETAIL_COLORS.free },
        ]
        : [
            { id: 'shortage', title: '수량부족', field: shortageField, color: AS_REPAIR_INBOUND_DETAIL_COLORS.shortage },
            { id: 'return', title: '반품수량', field: returnField, color: AS_REPAIR_INBOUND_DETAIL_COLORS.return },
            { id: 'paid', title: '유상수리수량', field: paidField, color: AS_REPAIR_INBOUND_DETAIL_COLORS.paid },
            { id: 'free', title: '무상수리수량', field: freeField, color: AS_REPAIR_INBOUND_DETAIL_COLORS.free },
        ];
    return {
        receivedTotal,
        receivedLabel: receivedField?.label || '실입고 수량',
        items: defs.map((def) => {
            const value = sumAsRepairQtyAcrossRows(rows, def.field, config);
            const percentOfReceived = receivedTotal > 0
                ? Math.round((value / receivedTotal) * 1000) / 10
                : 0;
            return {
                id: def.id,
                label: def.title,
                value,
                percentOfReceived,
                percentLabel: receivedTotal > 0
                    ? `실입고 대비 ${percentOfReceived}%`
                    : '-',
                color: def.color,
                fieldFound: !!def.field,
            };
        }),
    };
};

/** 검색·필터 결과 목록의 실입고·진행중·수리완료 수량 합계 및 100% 대비 비중 */
export const aggregateAsRepairQtyShare = (rows, listSummary, config, options = {}) => {
    const { managementScope = AS_REPAIR_MANAGEMENT_SCOPE.REPAIR } = options;
    const isPersonalScope = managementScope === AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL;
    const list = Array.isArray(rows) ? rows : [];
    const { totalField, inProgressField, doneField } = getAsRepairProgressQtyFields(listSummary);
    const { shortageField } = getAsRepairInboundBreakdownFields(config, listSummary);
    const defs = [
        { id: 'totalQty', title: '실입고 수량', field: totalField },
        ...(isPersonalScope ? [{ id: 'shortage', title: '수량부족', field: shortageField }] : []),
        { id: 'inProgressQty', title: '진행중 수량', field: inProgressField },
        { id: 'doneQty', title: '수리완료 수량', field: doneField },
    ];
    const sumFieldValue = (field) => (
        field
            ? list.reduce(
                (sum, row) => sum + parseQuantityValue(
                    getAsRepairFieldDisplayValue(field, row, config) ?? 0,
                ),
                0,
            )
            : 0
    );
    const totalQtyValue = sumFieldValue(totalField);
    const doneQtyValue = sumFieldValue(doneField);
    const segments = defs.map((def) => {
        const value = (isPersonalScope && def.id === 'totalQty')
            ? Math.max(0, totalQtyValue - doneQtyValue)
            : sumFieldValue(def.field);
        return {
            id: def.id,
            label: isPersonalScope
                ? (def.id === 'totalQty'
                    ? '미완료수량'
                    : def.id === 'shortage'
                        ? '수량부족'
                        : def.id === 'doneQty'
                            ? '수리완료수량'
                            : (def.field?.label || def.title))
                : (def.field?.label || def.title),
            value,
            color: AS_REPAIR_QTY_SHARE_COLORS[def.id] || '#94a3b8',
        };
    });
    const grandTotal = segments.reduce((sum, seg) => sum + seg.value, 0);
    const receivedSegment = segments.find((s) => s.id === 'totalQty') || null;
    const shortageSegment = segments.find((s) => s.id === 'shortage') || null;
    const inProgressSegment = segments.find((s) => s.id === 'inProgressQty') || null;
    const doneSegment = segments.find((s) => s.id === 'doneQty') || null;
    const visibleBaseSegments = isPersonalScope
        ? [receivedSegment, shortageSegment, doneSegment]
        : [inProgressSegment, doneSegment];
    const pairTotal = visibleBaseSegments.reduce((sum, seg) => sum + (seg?.value || 0), 0);
    const visibleSegments = visibleBaseSegments
        .filter(Boolean)
        .map((seg) => ({
            ...seg,
            percent: pairTotal > 0
                ? Math.round((seg.value / pairTotal) * 1000) / 10
                : 0,
        }));

    return {
        grandTotal,
        receivedTotal: receivedSegment?.value || 0,
        pairTotal,
        /** 막대 배경(100%, 실입고) — 화면에는 진행중·수리완료가 덮음 */
        receivedSegment: receivedSegment
            ? { ...receivedSegment, percent: 100 }
            : null,
        visibleLabel: isPersonalScope ? '미완료수량+수량부족+수리완료수량' : '진행중+수리완료',
        /** 막대·범례: 진행중 vs 수리완료만 합쳐 100% */
        visibleSegments,
        segments: segments.map((seg) => ({
            ...seg,
            percent: grandTotal > 0
                ? Math.round((seg.value / grandTotal) * 1000) / 10
                : 0,
        })),
        inboundBreakdown: aggregateAsRepairInboundBreakdown(list, listSummary, config, { managementScope }),
    };
};

export const withAsRepairShipCompleteFlag = (row) => {
    if (!row || typeof row !== 'object') {
        return row;
    }
    return {
        ...row,
        extra: {
            ...(row.extra && typeof row.extra === 'object' ? row.extra : {}),
            [AS_REPAIR_SHIP_COMPLETE_KEY]: true,
        },
    };
};

/** extra_json — 수량 확인중 / 진행중 탭 구분 (설정 항목 아님) */
export const AS_REPAIR_WORKFLOW_PHASE_KEY = '_asRepairPhase';
/** extra_json — 완료 탭에서 출고 완료 처리된 목록 */
export const AS_REPAIR_SHIP_COMPLETE_KEY = '_asRepairShipComplete';
/** extra_json — A/S 수리 관리(repair) vs A/S 개인 관리(personal) */
export const AS_REPAIR_MANAGEMENT_SCOPE_KEY = '_asRepairManagementScope';
export const AS_REPAIR_MANAGEMENT_SCOPE = {
    REPAIR: 'repair',
    PERSONAL: 'personal',
};
export const AS_REPAIR_INTERNAL_EXTRA_KEYS = new Set([
    AS_REPAIR_WORKFLOW_PHASE_KEY,
    AS_REPAIR_SHIP_COMPLETE_KEY,
    AS_REPAIR_MANAGEMENT_SCOPE_KEY,
]);

export const normalizeAsRepairManagementScope = (raw) => (
    String(raw || '').trim().toLowerCase() === AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL
        ? AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL
        : AS_REPAIR_MANAGEMENT_SCOPE.REPAIR
);

export const getAsRepairRowManagementScope = (row) => (
    normalizeAsRepairManagementScope(row?.extra?.[AS_REPAIR_MANAGEMENT_SCOPE_KEY])
);
export const isAsRepairInternalExtraKey = (key) => AS_REPAIR_INTERNAL_EXTRA_KEYS.has(key);
export const AS_REPAIR_WORKFLOW_PHASE = {
    QTY_CHECK: 'qty_check',
    IN_PROGRESS: 'in_progress',
};

export const getAsRepairRowWorkflowPhase = (row) => {
    const extra = row?.extra && typeof row.extra === 'object' ? row.extra : {};
    const phase = extra[AS_REPAIR_WORKFLOW_PHASE_KEY];
    if (phase === AS_REPAIR_WORKFLOW_PHASE.IN_PROGRESS) {
        return AS_REPAIR_WORKFLOW_PHASE.IN_PROGRESS;
    }
    if (phase === AS_REPAIR_WORKFLOW_PHASE.QTY_CHECK) {
        return AS_REPAIR_WORKFLOW_PHASE.QTY_CHECK;
    }
    return AS_REPAIR_WORKFLOW_PHASE.IN_PROGRESS;
};

export const isAsRepairRowShipComplete = (row) => {
    const extra = row?.extra && typeof row.extra === 'object' ? row.extra : {};
    const flag = extra[AS_REPAIR_SHIP_COMPLETE_KEY];
    return flag === true || flag === 'true' || flag === 1 || flag === '1';
};

/** 전체수량 === 수리완료 수량이면 완료 */
export const isAsRepairRowCompleted = (row, listSummary, config) => {
    const { totalField, doneField } = getAsRepairProgressQtyFields(listSummary);
    if (!totalField || !doneField) {
        return false;
    }
    const total = parseQuantityValue(getAsRepairFieldDisplayValue(totalField, row, config) ?? 0);
    const done = parseQuantityValue(getAsRepairFieldDisplayValue(doneField, row, config) ?? 0);
    return total === done;
};

/** qty_check | in_progress | done | ship_complete */
export const getAsRepairRowProgressTab = (row, listSummary, config) => {
    if (isAsRepairRowShipComplete(row)) {
        return 'ship_complete';
    }
    if (isAsRepairRowCompleted(row, listSummary, config)) {
        return 'done';
    }
    return getAsRepairRowWorkflowPhase(row);
};

export const filterAsRepairRowsByProgressTab = (rows, listSummary, config, tab) => {
    const list = Array.isArray(rows) ? rows : [];
    return list.filter((row) => getAsRepairRowProgressTab(row, listSummary, config) === tab);
};

export const countAsRepairRowsByProgressTab = (rows, listSummary, config) => {
    const list = Array.isArray(rows) ? rows : [];
    const counts = { qtyCheck: 0, inProgress: 0, done: 0, shipComplete: 0 };
    list.forEach((row) => {
        const tab = getAsRepairRowProgressTab(row, listSummary, config);
        if (tab === 'ship_complete') {
            counts.shipComplete += 1;
        } else if (tab === 'done') {
            counts.done += 1;
        } else if (tab === 'in_progress') {
            counts.inProgress += 1;
        } else {
            counts.qtyCheck += 1;
        }
    });
    return counts;
};

/** API 저장·수정용 — DB 행 객체를 폼 values 형태로 변환 */
export const asRepairRowToFormValues = (row, config) => {
    const values = {};
    normalizeAsRepairFormConfig(config).fields.forEach((field) => {
        if (field.inputType === 'auto') {
            values[field.key] = '';
            return;
        }
        const v = getRawAsRepairFieldValue(field, row);
        if (field.inputType === 'quantity') {
            values[field.key] = v == null ? 0 : parseQuantityValue(v);
        } else if (field.inputType === 'status') {
            values[field.key] = v == null ? 'received' : String(v);
        } else {
            values[field.key] = v == null ? '' : v;
        }
    });
    return values;
};
